import localforage from 'localforage';

const umuStorage = localforage.createInstance({
    name: 'umuStorage',
});

export enum STORAGE_VALUE {
    TRUE = '1',
    FALSE = '0',
}

export const getExpiresTime = (cacheDays: number) => {
    return Date.now() + cacheDays * 24 * 60 * 60 * 1000;
};

export const storageExpiresKey = 'umu_storage_expires';

export const updateExpiresTime = (key: string, cacheDays: number) => {
    try {
        const expiresTime = getExpiresTime(cacheDays);
        umuStorage
            .getItem(storageExpiresKey)
            .then((value: any) => {
                const localExpireRecord: Record<string, number> = value || {};
                localExpireRecord[key] = expiresTime;
                umuStorage.setItem(storageExpiresKey, localExpireRecord);
            })
            .catch(() => {
                // ignore
            });
    } catch (e) {
        // ignore
    }
};

export const isSupportLocalStorage = (() => {
    try {
        const isSupport = 'localStorage' in window && window.localStorage !== null;
        if (isSupport) {
            // safari隐身模式里边也不能用
            localStorage.setItem('__test', '1');
            localStorage.removeItem('__test');
        }
        return isSupport;
    } catch (e) {
        return false;
    }
})();

const cleanupExpiredStorage = async (cacheKeys: string[] = []) => {
    let localExpireRecord: Record<string, number> | null = null;
    try {
        localExpireRecord = await umuStorage.getItem(storageExpiresKey);
    } catch (error) {
        localExpireRecord = null;
    }

    if (!localExpireRecord || typeof localExpireRecord !== 'object') {
        return;
    }

    try {
        if (cacheKeys.length) {
            for (const key of cacheKeys) {
                delete localExpireRecord[key];
            }
        } else {
            const now = Date.now();
            for (const key in localExpireRecord) {
                if (localExpireRecord[key] < now) {
                    await umuStorageHelper.del(key);
                    delete localExpireRecord[key];
                }
            }
        }

        await umuStorage.setItem(storageExpiresKey, localExpireRecord);
    } catch (e) {
        // ignore
    }
};

export const umuStorageHelper = {
    storage: umuStorage,

    support: (() => {
        return isSupportLocalStorage || umuStorage.supports(umuStorage.INDEXEDDB);
    })(),

    async set<T>(
        name: string,
        value: T,
        options?: { cacheDays?: number; onError?: (error: Error) => void },
    ): Promise<boolean> {
        if (this.support) {
            try {
                await this.storage.setItem(name, value);
                const cacheDays = options?.cacheDays || 30;
                updateExpiresTime(name, cacheDays);
                return true;
            } catch (e) {
                options?.onError?.(e as Error);
                return false;
            }
        }
        return false;
    },

    async get<T>(name: string, onError?: (error: Error) => void): Promise<T | null> {
        try {
            if (this.support) {
                return await this.storage.getItem<T>(name);
            }
            return null;
        } catch (e) {
            onError?.(e as Error);
            return null;
        }
    },

    async del(name: string) {
        try {
            await this.storage.removeItem(name);
            await cleanupExpiredStorage([name]);
        } catch (e) {
            // ignore
        }
    },
};

if (typeof window !== 'undefined') {
    const cleanupCallback = () => {
        if (document.hidden || document.visibilityState === 'hidden') {
            cleanupExpiredStorage();
            window.removeEventListener('visibilitychange', cleanupCallback);
        }
    };
    window.addEventListener('visibilitychange', cleanupCallback);
}
